// «Regalos · elige al menos N para continuar» arriba del catálogo: las promos marcadas en el 04 que
// esperan su regalo (bloquean) y, sin borrador, las de regalo con varias opciones (opcionales).
import { useEffect, useState } from 'react';
import type { GiftsSlotContext } from '@/domains/sales/wizard/steps/BuildSaleStep';
import { Badge, Button, Modal } from '@/kit';
import { formatMXN } from '@/shared/lib/format/money';
import { useSalePromotionEvaluation } from '../api/useWizardPromotions';
import {
    GIFT_PENDING_REASON,
    PROMOTIONS_CONFIRMING_REASON,
    PROMOTIONS_RETRY_REASON,
} from '../constants';
import { useAnchorSaleContext } from '../hooks/useAnchorSaleContext';
import { useApplyPromotions } from '../hooks/useApplyPromotions';
import { pendingGiftsOf, withGiftChosen, type PendingGift } from '../lib/pendingGifts';
import { usePreviewSelection, useSessionPromotionState } from '../store/promotionSelectionStore';

export interface GiftsSectionProps extends GiftsSlotContext {
    sessionId: string;
}

const price = (cents: number): string => formatMXN(cents / 100);

/** Valor más bajo entre las opciones, para el precio tachado del renglón. */
function lowestValue(gift: PendingGift): number {
    const values = gift.options.map((o) => o.valueCents).filter((v) => v > 0);
    return values.length > 0 ? Math.min(...values) : 0;
}

export function GiftsSection({
    sessionId,
    setSelectionPending,
    onChangeInPhoneStep,
    ...context
}: GiftsSectionProps) {
    const { anchorLine, quoteState, confirmQuoteDiscard } = context;
    const { phoneDraft } = useSessionPromotionState(sessionId);
    const selection = usePreviewSelection(sessionId);
    const evaluation = useSalePromotionEvaluation(sessionId);
    const { withoutSureKeep } = useAnchorSaleContext(sessionId, anchorLine);
    const { apply, isApplying, error } = useApplyPromotions({ sessionId, context });
    const [openId, setOpenId] = useState<string>();
    const gifts = pendingGiftsOf({
        draft: phoneDraft,
        anchorSku: anchorLine.sku,
        anchorLineId: anchorLine.lineId,
        selection,
        evaluation: evaluation.data,
        withoutSureKeep,
    });
    const required = gifts.filter((g) => g.required);
    const requiredCount = required.length;
    // Sin evaluación no hay opciones que elegir: también bloquea, con su motivo.
    const reason =
        requiredCount === 0
            ? undefined
            : evaluation.isError
              ? PROMOTIONS_RETRY_REASON
              : evaluation.isLoading
                ? PROMOTIONS_CONFIRMING_REASON
                : GIFT_PENDING_REASON;
    useEffect(() => {
        setSelectionPending(reason !== undefined, reason);
    }, [reason, setSelectionPending]);
    useEffect(
        () => () => {
            setSelectionPending(false);
        },
        [setSelectionPending],
    );

    if (gifts.length === 0) return null;
    const charged = quoteState === 'charged';
    const opened = gifts.find((g) => g.promotionId === openId);
    const choose = (gift: PendingGift, sku: string): void => {
        setOpenId(undefined);
        confirmQuoteDiscard()
            .then((ok) =>
                ok
                    ? apply((base) => withGiftChosen(base, anchorLine.lineId, gift, sku))
                    : undefined,
            )
            .catch(() => undefined);
    };

    return (
        <section aria-label="Regalos" className="flex flex-col gap-2">
            <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    Regalos
                </span>
                <span className="text-[12px] text-muted-foreground">
                    {requiredCount > 0
                        ? `elige al menos ${String(requiredCount)} para continuar`
                        : 'opcionales'}
                </span>
            </div>
            <ul className="flex flex-col divide-y divide-border rounded-lg border border-border bg-card">
                {gifts.map((gift) => {
                    const available = gift.options.filter((o) => o.inStock).length;
                    const value = lowestValue(gift);
                    return (
                        <li
                            key={gift.promotionId}
                            className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-3 py-2.5"
                        >
                            <div className="min-w-0">
                                <div
                                    className="truncate text-[13px] font-semibold"
                                    title={gift.name}
                                >
                                    {gift.name}
                                </div>
                                <div className="text-[12px] text-muted-foreground">
                                    <span className="text-brand-accent">Regalo</span>
                                    {gift.options.length > 0 &&
                                        ` · ${String(gift.options.length)} variantes · ${String(available)} disponibles`}
                                </div>
                                {gift.unavailable && (
                                    <p role="status" className="text-[12px] text-warning">
                                        {gift.unavailable}
                                    </p>
                                )}
                            </div>
                            <div className="text-right text-[13px]">
                                {value > 0 && (
                                    <div className="text-[11px] text-muted-foreground line-through">
                                        {price(value)}
                                    </div>
                                )}
                                <div className="font-semibold text-success">{price(0)}</div>
                            </div>
                            {gift.unavailable ? (
                                <Button
                                    size="sm"
                                    variant="link"
                                    disabled={charged}
                                    onClick={onChangeInPhoneStep}
                                >
                                    Cambiar en el paso 04
                                </Button>
                            ) : (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={
                                        charged ||
                                        isApplying ||
                                        available === 0 ||
                                        gift.promotionVersion === null
                                    }
                                    onClick={() => {
                                        setOpenId(gift.promotionId);
                                    }}
                                >
                                    Elegir
                                </Button>
                            )}
                        </li>
                    );
                })}
            </ul>
            {error && (
                <p role="alert" className="text-[12px] text-destructive">
                    {error}
                </p>
            )}
            <Modal
                isOpen={opened !== undefined}
                onClose={() => {
                    setOpenId(undefined);
                }}
                title={opened?.name}
                titleAction={<Badge>Regalo</Badge>}
            >
                {opened && (
                    <ul className="flex flex-col divide-y divide-border">
                        {opened.options.map((option) => (
                            <li
                                key={option.sku}
                                className="flex items-center justify-between gap-3 py-2.5"
                            >
                                <div className="min-w-0">
                                    <div className="truncate text-[13px] font-semibold">
                                        {option.name}
                                    </div>
                                    <div className="text-[12px] text-muted-foreground">
                                        {option.inStock
                                            ? `Valor ${price(option.valueCents)} · a ${price(0)}`
                                            : 'Sin existencia'}
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={!option.inStock}
                                    aria-label={`Elegir ${option.name}`}
                                    onClick={() => {
                                        choose(opened, option.sku);
                                    }}
                                >
                                    Elegir
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}
            </Modal>
        </section>
    );
}
